import { useEffect, useState } from "react";
import { api } from "../api.js";

interface Pending {
  id: string;
  displayNameSv: string;
  rawName: string;
  occurrences: number;
  createdAt: string;
}

interface Proposal {
  nameSv: string;
  nameEn: string;
  category: string;
  defaultUnit: string;
  kcal: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
  saturatedFatG: number;
  fiberG: number;
  sugarG: number;
  saltG: number;
  densityGPerMl: number | null;
  gramsPerPiece: number | null;
  allergens: string[];
  isVegan: boolean;
  isVegetarian: boolean;
  containsGluten: boolean;
  containsLactose: boolean;
  isPork: boolean;
  isBeef: boolean;
  isAlcohol: boolean;
  confidence?: number;
}

const UNITS = [
  "GRAM",
  "KILOGRAM",
  "MILLILITER",
  "DECILITER",
  "LITER",
  "TEASPOON",
  "TABLESPOON",
  "COUNT",
  "SLICE",
  "CLOVE",
  "PINCH",
];
const NUM_FIELDS: Array<[keyof Proposal, string]> = [
  ["kcal", "kcal / 100 g"],
  ["proteinG", "Protein g"],
  ["carbsG", "Kolhydrater g"],
  ["fatG", "Fett g"],
  ["saturatedFatG", "varav mättat g"],
  ["fiberG", "Fiber g"],
  ["sugarG", "Socker g"],
  ["saltG", "Salt g"],
  ["densityGPerMl", "Densitet g/ml (valfri)"],
  ["gramsPerPiece", "Gram/styck (valfri)"],
];
const BOOL_FIELDS: Array<[keyof Proposal, string]> = [
  ["isVegan", "Vegansk"],
  ["isVegetarian", "Vegetarisk"],
  ["containsGluten", "Gluten"],
  ["containsLactose", "Laktos"],
  ["isPork", "Fläsk"],
  ["isBeef", "Nöt"],
  ["isAlcohol", "Alkohol"],
];
const ALLERGENS = [
  "gluten",
  "crustaceans",
  "eggs",
  "fish",
  "peanuts",
  "soy",
  "milk",
  "tree_nuts",
  "celery",
  "mustard",
  "sesame",
  "sulphites",
  "lupin",
  "molluscs",
];

function emptyProposal(nameSv: string): Proposal {
  return {
    nameSv,
    nameEn: "",
    category: "",
    defaultUnit: "GRAM",
    kcal: 0,
    proteinG: 0,
    carbsG: 0,
    fatG: 0,
    saturatedFatG: 0,
    fiberG: 0,
    sugarG: 0,
    saltG: 0,
    densityGPerMl: null,
    gramsPerPiece: null,
    allergens: [],
    isVegan: false,
    isVegetarian: false,
    containsGluten: false,
    containsLactose: false,
    isPork: false,
    isBeef: false,
    isAlcohol: false,
  };
}

export function PendingIngredientsPage() {
  const [pending, setPending] = useState<Pending[]>([]);
  const [forms, setForms] = useState<Record<string, Proposal>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    api<{ pending: Pending[] }>("/admin/v1/pending-ingredients")
      .then((r) => setPending(r.pending))
      .catch((e) => setError(String(e.message)));
  };
  useEffect(load, []);

  const setBusyFor = (id: string, v: boolean) => setBusy((b) => ({ ...b, [id]: v }));
  const openForm = (id: string, initial: Proposal) => setForms((f) => ({ ...f, [id]: initial }));
  const update = (id: string, patch: Partial<Proposal>) =>
    setForms((f) => {
      const cur = f[id];
      return cur ? { ...f, [id]: { ...cur, ...patch } } : f;
    });

  const propose = async (p: Pending) => {
    setBusyFor(p.id, true);
    setError(null);
    try {
      const r = await api<{ proposal: Proposal }>(`/admin/v1/pending-ingredients/${p.id}/propose`, {
        method: "POST",
      });
      setForms((f) => ({ ...f, [p.id]: { ...emptyProposal(p.displayNameSv), ...r.proposal } }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyFor(p.id, false);
    }
  };

  const commit = async (p: Pending) => {
    const form = forms[p.id];
    if (!form) return;
    setBusyFor(p.id, true);
    setError(null);
    try {
      await api(`/admin/v1/pending-ingredients/${p.id}/commit`, { method: "POST", body: form });
      setPending((list) => list.filter((x) => x.id !== p.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyFor(p.id, false);
    }
  };

  const dismiss = async (p: Pending) => {
    setBusyFor(p.id, true);
    setError(null);
    try {
      await api(`/admin/v1/pending-ingredients/${p.id}/dismiss`, { method: "POST" });
      setPending((list) => list.filter((x) => x.id !== p.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyFor(p.id, false);
    }
  };

  return (
    <div>
      <h2>Nya ingredienser</h2>
      <p className="muted">
        Ingredienser från inskickade recept som inte fanns i databasen. Låt Gemini föreslå
        näringsvärden, granska/justera och lägg till – då matchar de i alla framtida recept. AI
        hittar aldrig på värden utan din kontroll (sparas som verifierat av admin).
      </p>
      {error && <div className="error-text">{error}</div>}
      {pending.length === 0 && <div className="panel">Inga nya ingredienser väntar. 🎉</div>}
      {pending.map((p) => {
        const form = forms[p.id];
        return (
          <div className="panel" key={p.id}>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <div>
                <strong>{p.displayNameSv}</strong> <span className="badge">{p.occurrences}×</span>
                <div className="muted">rå: {p.rawName}</div>
              </div>
              {!form && (
                <div className="row">
                  <button className="primary" disabled={busy[p.id]} onClick={() => propose(p)}>
                    {busy[p.id] ? "Frågar Gemini…" : "Föreslå värden (Gemini)"}
                  </button>
                  <button
                    className="ghost"
                    onClick={() => openForm(p.id, emptyProposal(p.displayNameSv))}
                  >
                    Fyll i manuellt
                  </button>
                  <button className="ghost danger" disabled={busy[p.id]} onClick={() => dismiss(p)}>
                    Avfärda
                  </button>
                </div>
              )}
            </div>

            {form && (
              <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
                {form.confidence != null && (
                  <div className="muted">
                    Gemini-säkerhet: {Math.round(form.confidence * 100)}% – kontrollera värdena.
                  </div>
                )}
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  <label>
                    Namn (sv){" "}
                    <input
                      value={form.nameSv}
                      onChange={(e) => update(p.id, { nameSv: e.target.value })}
                    />
                  </label>
                  <label>
                    Namn (en){" "}
                    <input
                      value={form.nameEn}
                      onChange={(e) => update(p.id, { nameEn: e.target.value })}
                    />
                  </label>
                  <label>
                    Kategori{" "}
                    <input
                      value={form.category}
                      onChange={(e) => update(p.id, { category: e.target.value })}
                    />
                  </label>
                  <label>
                    Enhet{" "}
                    <select
                      value={form.defaultUnit}
                      onChange={(e) => update(p.id, { defaultUnit: e.target.value })}
                    >
                      {UNITS.map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  {NUM_FIELDS.map(([key, label]) => (
                    <label
                      key={key as string}
                      style={{ display: "flex", flexDirection: "column", fontSize: 12 }}
                    >
                      {label}
                      <input
                        type="number"
                        style={{ width: 100 }}
                        value={(form[key] as number | null) ?? ""}
                        onChange={(e) =>
                          update(p.id, {
                            [key]: e.target.value === "" ? null : Number(e.target.value),
                          } as Partial<Proposal>)
                        }
                      />
                    </label>
                  ))}
                </div>
                <div className="row" style={{ gap: 12, flexWrap: "wrap" }}>
                  {BOOL_FIELDS.map(([key, label]) => (
                    <label key={key as string} style={{ fontSize: 12 }}>
                      <input
                        type="checkbox"
                        checked={form[key] as boolean}
                        onChange={(e) =>
                          update(p.id, { [key]: e.target.checked } as Partial<Proposal>)
                        }
                      />{" "}
                      {label}
                    </label>
                  ))}
                </div>
                <div className="row" style={{ gap: 12, flexWrap: "wrap" }}>
                  <span className="muted" style={{ fontSize: 12 }}>
                    Allergener:
                  </span>
                  {ALLERGENS.map((a) => (
                    <label key={a} style={{ fontSize: 12 }}>
                      <input
                        type="checkbox"
                        checked={form.allergens.includes(a)}
                        onChange={(e) =>
                          update(p.id, {
                            allergens: e.target.checked
                              ? [...form.allergens, a]
                              : form.allergens.filter((x) => x !== a),
                          })
                        }
                      />{" "}
                      {a}
                    </label>
                  ))}
                </div>
                <div className="row">
                  <button className="primary" disabled={busy[p.id]} onClick={() => commit(p)}>
                    {busy[p.id] ? "Lägger till…" : "Lägg till i databasen"}
                  </button>
                  <button
                    className="ghost"
                    onClick={() =>
                      setForms((f) => {
                        const n = { ...f };
                        delete n[p.id];
                        return n;
                      })
                    }
                  >
                    Avbryt
                  </button>
                  <button className="ghost danger" disabled={busy[p.id]} onClick={() => dismiss(p)}>
                    Avfärda
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
